import { useState, useEffect } from "react"
import get from "lodash/get"
import isFunction from "lodash/isFunction"

import { isDevelopment } from "constants/internal"

/**
 * @function useDynamicImport
 * @param {func}   loader  | function used to import the module (remember to memoize it)
 * @param {string} segment | the segment of the module that needs to be imported
 * @param {bool}   start   | whether or not the import should start
 */
const useDynamicImport = (loader, segment = "default", start = true) => {
    if (!loader || !isFunction(loader)) {
        // Type error for the useDynamicImport first parameter
        throw Error(
            "useDynamicImport requires a function returning a Promise as first parameter.",
        )
    }

    const [done, setDone] = useState(false)
    const [DynamicModule, setDynamicModule] = useState(null)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!start) return

        loader()
            .then(module => {
                const importedModule = get(module, segment)

                setDynamicModule(() => importedModule)
                setDone(true)
            })
            .catch(err => {
                setError(err)
                setDone(true)
                // eslint-disable-next-line no-console
                if (isDevelopment) console.error(err)
            })
    }, [loader, start])

    // Effect used to determine if the module has done with the import
    useEffect(() => {
        if (DynamicModule && !done) setDone(true)

        if (done && !DynamicModule) setDone(false)
    }, [DynamicModule, done])

    return [DynamicModule, done, error]
}

export default useDynamicImport
